import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { styles } from '../styles';
import { SectionWrapper } from '../hoc';
import { fadeIn, textVariant } from '../utils/motion';

const StatCard = ({ label, value, delay }) => (
	<motion.div
		variants={fadeIn('up', 'tween', delay, 0.4)}
		className="text-center p-4">
		<p className="text-primary text-[28px] sm:text-[32px] font-bold tracking-tight">
			{value}
		</p>
		<p className="text-muted text-[13px] font-medium mt-1">{label}</p>
	</motion.div>
);

const GitHubContributions = () => {
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [stats, setStats] = useState({
		contributions: 0, commits: 0, pullRequests: 0,
		codeReviews: 0, repositories: 0, languages: [],
		topLanguages: [], currentStreak: 0, longestStreak: 0,
		totalStars: 0, lastUpdated: '', message: ''
	});

	useEffect(() => {
		const fetchGitHubStats = async () => {
			try {
				const response = await fetch('/github-stats.json');
				if (!response.ok) throw new Error('Failed to fetch GitHub stats');
				const data = await response.json();
				setStats(data);
			} catch (err) {
				setError(err.message);
			} finally {
				setLoading(false);
			}
		};
		fetchGitHubStats();
	}, []);

	const statItems = [
		{ label: 'Contributions', value: stats.contributions },
		{ label: 'Commits', value: stats.commits },
		{ label: 'Pull Requests', value: stats.pullRequests },
		{ label: 'Code Reviews', value: stats.codeReviews },
		{ label: 'Current Streak', value: stats.currentStreak },
		{ label: 'Repositories', value: stats.repositories },
	];

	return (
		<>
			<motion.div variants={textVariant()}>
				<p className={styles.sectionSubText}>Activity</p>
				<h2 className={styles.sectionHeadText}>GitHub</h2>
			</motion.div>

			<div className="mt-8">
				{loading ? (
					<p className="text-muted text-center py-8">Loading stats...</p>
				) : error ? (
					<p className="text-muted text-center py-8">Stats temporarily unavailable</p>
				) : (
					<>
						{stats.message && (
							<motion.p
								variants={fadeIn('', '', 0.05, 0.4)}
								className="text-muted text-[14px] mb-6">
								{stats.message}
							</motion.p>
						)}

						<div className="grid grid-cols-3 sm:grid-cols-6 gap-1 border border-border rounded-xl overflow-hidden bg-surface">
							{statItems.map((item, i) => (
								<StatCard key={item.label} {...item} delay={i * 0.05} />
							))}
						</div>

						{stats.topLanguages.length > 0 && (
							<motion.div
								variants={fadeIn('', '', 0.3, 0.4)}
								className="mt-6 flex flex-wrap items-center gap-2">
								<span className="text-muted text-[14px] font-medium mr-2">Top Languages:</span>
								{stats.topLanguages.map((lang, i) => (
									<span key={i} className="px-3 py-1 bg-accent/5 border border-accent/15 rounded-md text-[13px] text-accent font-medium">
										{lang.name}
									</span>
								))}
							</motion.div>
						)}

						<motion.div
							variants={fadeIn('', '', 0.35, 0.4)}
							className="mt-6 flex items-center justify-between">
							<p className="text-muted text-[13px]">
								Many more contributions in private repositories
							</p>
							<a
								href="https://github.com/ersachinsaurav"
								target="_blank"
								rel="noopener noreferrer"
								className="text-accent text-[14px] font-medium hover:underline">
								View Profile &rarr;
							</a>
						</motion.div>
					</>
				)}
			</div>
		</>
	);
};

export default SectionWrapper(GitHubContributions, 'github');
